import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { SERVICES, QUOTE_SERVICES, IMAGES } from "@/data/content";

const Pricing = () => {
  usePageMeta(
    "Pricing — Detailing Packages from $99 | Luxpress Detailing",
    "Full detailing package pricing: Basic Exterior Wash from $99, Mini Detail from $150, Full Detail from $250, Pre-Sale Detail from $500. South Melbourne."
  );
  return (
    <div data-testid="pricing-page">
      <section className="mx-auto max-w-7xl px-5 pt-36 sm:px-8 sm:pt-44">
        <Reveal>
          <p className="kicker">Pricing</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter sm:text-6xl">
            The ladder.<br /><span className="text-mint">Pick a rung.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-chrome">
            All prices in AUD, all "from" prices — final quote depends on vehicle size and condition.
            Every price on this page is real, selectable text. Nothing is baked into an image.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8" data-testid="pricing-ladder">
        <div className="border-t border-teal-line">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.05}>
              <div className="grid gap-6 border-b border-teal-line py-10 transition-colors duration-300 hover:bg-panel sm:px-4 lg:grid-cols-[4rem_1.4fr_1fr_auto] lg:items-center lg:gap-10">
                <span className="font-mono text-sm text-chrome/60">0{i + 1}</span>
                <div>
                  <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">{s.name}</h2>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-chrome">{s.covers}</p>
                </div>
                <div className="flex gap-10 lg:justify-end">
                  <div>
                    <p className="spec-label">Duration</p>
                    <p className="mt-1 font-mono text-sm text-white">{s.duration}</p>
                  </div>
                  <div>
                    <p className="spec-label">From</p>
                    <p className="mt-1 font-mono text-2xl font-bold text-mint sm:text-3xl" data-testid={`price-${s.id}`}>${s.price}</p>
                  </div>
                </div>
                <Link to={`/contact?service=${s.id}`} data-testid={`pricing-book-${s.id}`} className="btn-ghost w-fit lg:justify-self-end">
                  Book this <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pt-16 sm:px-8">
        <Reveal>
          <img
            src={IMAGES.foam}
            alt="Machine polishing under way in the Luxpress studio"
            loading="lazy"
            className="aspect-[16/10] w-full border border-teal-line object-cover sm:aspect-[21/8]"
          />
        </Reveal>
      </section>

      <Marquee text="Every price real text. Every quote in writing." />

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="pricing-quote-services">
        <Reveal>
          <p className="kicker">Quoted per vehicle</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold uppercase tracking-tight sm:text-4xl">
            No honest price without seeing the car
          </h2>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-chrome sm:text-base">
            Protection work depends on condition, coverage and material. These services are quoted after a
            consultation — in person at the studio, or from photos you send us.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-px border border-teal-line bg-teal-line sm:grid-cols-2 lg:grid-cols-3">
          {QUOTE_SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.05} className="bg-obsidian p-8 transition-colors duration-300 hover:bg-panel">
              <span className="font-mono text-xs text-mint">0{i + 6}</span>
              <h3 className="mt-4 font-display text-lg font-bold uppercase tracking-tight">{s.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-chrome">{s.blurb}</p>
              <Link
                to={s.path || `/contact?service=${s.id}`}
                data-testid={`pricing-quote-${s.id}`}
                className="group mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-mint"
              >
                {s.path ? "View service" : "Request a quote"}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Pricing;
